import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/AuthContext";
import { whitelistAPI } from "../services/api";
import AddIPModal from "../components/AddIPModal";
import WhitelistTable from "../components/WhitelistTable";

function Dashboard() {
  const [whitelist, setWhitelist] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchWhitelist();
  }, []);


  const fetchWhitelist = async () => {
    try {
      setIsLoading(true);
      const response = await whitelistAPI.getAll();
      setWhitelist(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch whitelist");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddIP = async (ipData) => {
    try {
      const response = await whitelistAPI.add(ipData);
      toast.success(`IP added successfully! BitNinja: ${response.data.bitninja_status}`);
      fetchWhitelist();
      setShowAddModal(false);
    } catch (error) {
      const message = error.response?.data?.error || "Failed to add IP";
      toast.error(message);
    }
  };

  const handleRemoveIP = async (id, ipAddress) => {
    if (!window.confirm(`Are you sure you want to remove ${ipAddress} from the whitelist?`)) {
      return;
    }

    try {
      await whitelistAPI.remove(id);
      toast.success("IP removed successfully!");
      fetchWhitelist();
    } catch (error) {
      const message = error.response?.data?.error || "Failed to remove IP";
      toast.error(message);
    }
  };

  const handleSync = async () => {
    if (isSyncing) {
      toast.warning("Sync already in progress. Please wait...");
      return;
    }
    
    try {
      setIsSyncing(true);
      setSyncProgress(0);
      
      // Progress simulation
      const progressInterval = setInterval(() => {
        setSyncProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const response = await whitelistAPI.sync();
      
      clearInterval(progressInterval);
      setSyncProgress(100);
      
      // Fix: Use correct response field names
      const added = response.data.added || 0;
      const updated = response.data.updated || 0;
      const removed = response.data.removed || 0;
      const total = response.data.total || 0;
      
      toast.success(`Sync completed! Added: ${added}, Updated: ${updated}, Removed: ${removed}, Total: ${total}`);
      fetchWhitelist(); // Refresh the list
      
      // Reset progress after a short delay
      setTimeout(() => setSyncProgress(0), 1000);
      
    } catch (error) {
      setSyncProgress(0);
      const message = error.response?.data?.error || "Failed to sync with BitNinja";
      toast.error(message);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLogout = () => {
    // Clear everything manually
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    
    // Call logout from context
    logout();
    toast.info("Logged out successfully");
    
    // Force navigation
    window.location.href = '/login';
  };


  return (
    <div className="dashboard">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark">
        <div className="container-fluid">
          <a className="navbar-brand" href="#">
            <i className="fas fa-shield-alt me-2"></i>
            Silvera Firewall
          </a>
          <div className="navbar-nav ms-auto d-flex align-items-center">
            {/* User info */}
            <span className="text-white me-3">
              <i className="fas fa-user me-1"></i>
              Welcome, {user?.username || 'Loading...'}
            </span>
            
            {/* Professional logout button */}
            <button 
              className="btn btn-outline-light me-2" 
              onClick={handleLogout}
              style={{ 
                minWidth: '90px',
                fontWeight: '500'
              }}
            >
              <i className="fas fa-sign-out-alt me-2"></i>
              Logout
            </button>
            
            {/* Alternative exit button */}
            <button 
              className="btn btn-danger btn-sm" 
              onClick={handleLogout}
              title="Exit Application"
            >
              <i className="fas fa-power-off"></i>
            </button>
          </div>
        </div>
      </nav>


      {/* Main Content */}
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2>
                  <i className="fas fa-list me-2"></i>
                  IP Whitelist Management
                </h2>
                <p className="text-muted mb-0">
                  Manage IP addresses with BitNinja integration
                </p>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-info position-relative"
                  onClick={handleSync}
                  disabled={isSyncing}
                >
                  {isSyncing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Syncing... {syncProgress}%
                      {syncProgress > 0 && (
                        <div 
                          className="position-absolute bottom-0 start-0 bg-info" 
                          style={{
                            height: '2px',
                            width: `${syncProgress}%`,
                            transition: 'width 0.3s ease'
                          }}
                        ></div>
                      )}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sync-alt me-2"></i>
                      Sync BitNinja
                    </>
                  )}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowAddModal(true)}
                >
                  <i className="fas fa-plus me-2"></i>
                  Add IP Address
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <i className="fas fa-list fa-2x text-primary"></i>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <div className="text-muted small">Total IPs</div>
                        <div className="h4 mb-0">{whitelist.length}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <i className="fas fa-check-circle fa-2x text-success"></i>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <div className="text-muted small">Active</div>
                        <div className="h4 mb-0">
                          {whitelist.filter(ip => ip.is_active).length}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <i className="fas fa-shield-alt fa-2x text-info"></i>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <div className="text-muted small">BitNinja Protected</div>
                        <div className="h4 mb-0">
                          {whitelist.filter(ip => ip.bitninja_status === 'whitelisted').length}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        <i className="fas fa-exclamation-triangle fa-2x text-warning"></i>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <div className="text-muted small">Failed</div>
                        <div className="h4 mb-0">
                          {whitelist.filter(ip => ip.bitninja_status === 'failed').length}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sync Information */}
            <div className="alert alert-info d-flex align-items-center mb-4">
              <i className="fas fa-info-circle me-3"></i>
              <div>
                <strong>Auto-Sync Enabled:</strong> BitNinja whitelist automatically syncs every 10 minutes. 
                Use the "Sync BitNinja" button above for immediate synchronization.
              </div>
            </div>

            {/* Whitelist Table */}
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <WhitelistTable
                  whitelist={whitelist}
                  isLoading={isLoading}
                  onRemove={handleRemoveIP}
                  onRefresh={fetchWhitelist}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add IP Modal */}
      <AddIPModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddIP}
      />
    </div>
  );
}

export default Dashboard;